import { stripLocalePrefix } from "@/i18n/routing"

// Paths a logged-out visitor can open without the signup wall
// (components/auth/signup-wall.tsx). Lives here, free of client imports, so
// tests/public-paths.test.ts can check every marketing page against it: a page
// missing from this list ships with the signup modal over it, which is how
// /careers once went live walled.

// Reachable without an account: the homepage, open marketing pages, the
// public booking funnel (/book, /shop), post-checkout pages (/payment),
// auth + legal, and design-QA (/preview). Everything else is walled.
//
// Keep this list in sync with lib/supabase/middleware.ts publicRoutes —
// middleware only guards /dashboard, but this wall is what logged-out users
// actually hit on marketing and booking surfaces in production.
export const PUBLIC_EXACT: ReadonlyArray<string> = ["/"]
// "/book" = the public client booking funnel (browse + book before any login).
// "/clients" = the client entry door (sign-in / create-account for consumers) —
// must stay public, else the wall drops the PRO signup over it.
export const PUBLIC_PREFIXES: ReadonlyArray<string> = [
  "/auth",
  "/invite",
  // A claim link IS the signup. Walled, it dropped a generic "Create account"
  // over the owner's own page — and an owner who completed THAT got a fresh
  // empty account while their shop stayed on the placeholder, which is exactly
  // how the first seeded handover went wrong.
  "/claim",
  "/privacy",
  "/terms",
  "/refunds",
  // Twilio toll-free verification reviewers open this logged out.
  "/sms",
  "/book",
  "/shop",
  "/payment",
  "/clients",
  // Open marketing surfaces — reachable logged-out by design.
  "/pricing",
  "/press",
  "/blog",
  // The posts themselves. /blog is only the index — every card on it links to
  // /articles/[slug], so listing /blog alone opened the shelf and walled the
  // books: the list rendered, then the signup modal dropped the moment anyone
  // clicked through. middleware.ts has always treated /articles as public;
  // this is the client-side wall catching up to it.
  "/articles",
  "/help",
  "/listings",
  // The search page is the front door for barbers — it has its own
  // "Create free account" close at the foot of the results. Walling it
  // dropped the modal before anyone had seen a single shop.
  "/search",
  "/barbers",
  "/hiring",
  "/rewards",
  "/compare",
  // City answer pages (barber booth rental in <city>) and their index.
  "/booth-rental",
  "/about",
  "/contact",
  "/traction",
  "/changelog",
  // Status must stay reachable during an outage — walling it defeats the point.
  "/status",
  // The unsubscribe link in marketing email. It must work without an account
  // session — that is the whole point of a one-step opt-out.
  "/unsubscribe",
  // Design-QA surfaces. Unlisted and noindex'd, but the wall would drop the
  // signup modal over them on a deployed preview, which makes them useless
  // for reviewing a design.
  "/preview",
]

export function isPublicPath(rawPathname: string | null): boolean {
  if (!rawPathname) return true
  // Locale-agnostic: the Spanish landing /es must stay open exactly like /.
  const pathname = stripLocalePrefix(rawPathname)
  if (PUBLIC_EXACT.includes(pathname)) return true
  return PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

// The public routes lib/supabase/middleware.ts lets through without a session.
// Kept next to the wall's list so the two can be checked together.
export const SERVER_PUBLIC_ROUTES: ReadonlyArray<string> = [
  "/",
  "/auth/login",
  "/auth/signup",
  "/auth/callback",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/blog",
  "/articles",
  "/help",
  "/press",
  "/traction",
  "/changelog",
  "/privacy",
  "/terms",
  "/refunds",
  // Twilio toll-free verification reviewers open this logged out.
  "/sms",
  "/pricing",
  "/listings",
  "/search",
  "/barbers",
  "/hiring",
  "/rewards",
  "/compare",
  "/booth-rental",
  "/book",
  "/about",
  "/contact",
  "/invite",
  "/unsubscribe",
]
